// src/components/FamilyDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { db, storage, functions } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { collection, query, where, orderBy, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
// Using React Bootstrap components
import { Container, Row, Col, Card, Spinner, Alert, Button, Breadcrumb, Table } from 'react-bootstrap';
// Image from react-bootstrap is no longer needed here if only used for member photos
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencilAlt, faTrashAlt, faImages } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar'; // Import the new component
import FormattedPhoneNumber from './FormattedPhoneNumber'; // Import the new FormattedPhoneNumber component

// Initialize Firebase Functions
const deleteMemberCallable = httpsCallable(functions, 'deleteMember');
const deleteFamilyCallable = httpsCallable(functions, 'deleteFamily');

// This placeholder is for the main family photo, MemberPhotoAvatar has its own internal default.
const MAIN_FAMILY_PHOTO_PLACEHOLDER = '/assets/blank.png';


// Helper: Format Birthday (Month/Day)
const formatBirthday = (dateValue) => {
    if (typeof dateValue === 'string' && dateValue.match(/^\d{2}\/\d{2}$/)) { return dateValue; }
    if (dateValue instanceof Timestamp) {
        try { return format(dateValue.toDate(), 'MM/dd'); }
        catch (e) { console.error("Error formatting timestamp:", dateValue, e); return ''; }
    }
    return '';
};


function FamilyDetail() {
    const { familyId } = useParams();
    const navigate = useNavigate();
    const { currentUser, isAdmin, userFidClaim, loadingAuth } = useAuth();

    // State for Family Data
    const [family, setFamily] = useState(null);
    const [photoURL, setPhotoURL] = useState(MAIN_FAMILY_PHOTO_PLACEHOLDER);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for Member Data
    const [members, setMembers] = useState([]);
    // This will store the GCS path or direct HTTP URL from Firestore for each member.
    // MemberPhotoAvatar will handle resolving GCS paths.
    const [memberOriginalPhotoPaths, setMemberOriginalPhotoPaths] = useState({});
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [errorMembers, setErrorMembers] = useState(null);

    // Effect 1: Fetch Family Data & Photo URL
    useEffect(() => {
        if (loadingAuth || !familyId) {
             if (!loadingAuth && !familyId) { setError("Family ID missing."); setLoading(false); setLoadingMembers(false); }
             return;
        }
        setLoading(true); setLoadingMembers(true); setError(null); setErrorMembers(null); setFamily(null); setMembers([]); setPhotoURL(MAIN_FAMILY_PHOTO_PLACEHOLDER);
        setMemberOriginalPhotoPaths({}); // Reset member photo paths on familyId change
        let isMounted = true;
        const fetchFamilyAndPhoto = async () => {
            try {
                const familyDocRef = doc(db, 'family', familyId);
                const familyDocSnapshot = await getDoc(familyDocRef);
                if (!isMounted) return;
                if (familyDocSnapshot.exists()) {
                    const familyData = { ...familyDocSnapshot.data(), id: familyDocSnapshot.id };
                    setFamily(familyData);
                    if (familyData.photo) {
                         if (familyData.photo.startsWith('http')) {
                            if (isMounted) setPhotoURL(familyData.photo);
                         } else {
                             try {
                                const photoRef = ref(storage, familyData.photo);
                                const url = await getDownloadURL(photoRef);
                                if (isMounted) setPhotoURL(url);
                             } catch (photoError) {
                                if (isMounted && photoError.code !== 'storage/object-not-found') {
                                  console.error(`Error getting photo URL for ${familyData.photo}:`, photoError);
                                }
                                // photoURL remains MAIN_FAMILY_PHOTO_PLACEHOLDER
                             }
                         }
                    } // else photoURL remains MAIN_FAMILY_PHOTO_PLACEHOLDER
                } else { if (isMounted) setError('Family not found.'); if (isMounted) setLoadingMembers(false); }
            } catch (err) {
                console.error("Error fetching family:", err);
                if (isMounted) setError(err.message || "An error occurred fetching family details.");
                if (isMounted) setLoadingMembers(false);
            } finally { if (isMounted) setLoading(false); }
        };
        fetchFamilyAndPhoto();
        return () => { isMounted = false; };
    }, [familyId, loadingAuth, currentUser]);

    // Effect 3: Fetch Members. Store the raw 'photo' field (GCS path or URL).
    useEffect(() => {
        if (!family || !family.id || !family.orgId) {
            setLoadingMembers(false);
            setMembers([]);
            setMemberOriginalPhotoPaths({});
            return;
        }
        const currentFamilyId = family.id;
        const currentOrgId = family.orgId;
        let isMounted = true;

        const fetchMembersData = async () => {
            if (!isMounted) return;
            setLoadingMembers(true);
            setErrorMembers(null);
            try {
                const membersCollection = collection(db, 'member');
                const q = query(membersCollection, where('familyId', '==', currentFamilyId), where('orgId', '==', currentOrgId), orderBy('name'));
                const memberSnapshot = await getDocs(q);
                if (!isMounted) return;

                const memberList = memberSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
                setMembers(memberList);

                const photoPathsMap = {};
                memberList.forEach(member => {
                    if (member.id) {
                        photoPathsMap[member.id] = member.photo; // Store GCS path, HTTP URL, or null/undefined
                    }
                });
                if (isMounted) {
                    setMemberOriginalPhotoPaths(photoPathsMap);
                }

            } catch (err) {
                 if (isMounted) {
                    if (err.code === 'failed-precondition' && err.message?.includes('index')) { setErrorMembers("Database index missing. Check console."); console.error("Index Required:", err); }
                    else { setErrorMembers(err.message || "Failed to load family members."); }
                    console.error(`Error fetching members for family ${currentFamilyId}:`, err);
                 }
            } finally { if (isMounted) setLoadingMembers(false); }
        };

        fetchMembersData();
        return () => { isMounted = false; };
    }, [family]);

    // Handler: Delete Member
    const handleDeleteMember = async (memberIdToDelete, memberName) => {
        if (!window.confirm(`Are you sure you want to permanently delete ${memberName || 'this member'}?`)) return;
        setError(null); setErrorMembers(null);
        try { const result = await deleteMemberCallable({ memberId: memberIdToDelete });
            if (result?.data?.success) {
                setMembers(prev => prev.filter(m => m.id !== memberIdToDelete));
                setMemberOriginalPhotoPaths(prev => {
                    const newPaths = {...prev};
                    delete newPaths[memberIdToDelete];
                    return newPaths;
                });
            } else { setErrorMembers(result?.data?.message || "Server denied the member deletion request."); }
        }
        catch (err) {
             setError(`Failed to delete member: ${err.message || "Network/Function error"}`);
        }
    };

    // Handler: Delete Family (remains unchanged)
    const handleDeleteFamily = async (familyIdToDelete, familyName) => {
        if (!window.confirm(`REMOVE FAMILY\n\nAre you absolutely sure...? \n\nWARNING: This will also delete ALL members...`)) return;
        setError(null); setErrorMembers(null);
        try { const result = await deleteFamilyCallable({ familyId: familyIdToDelete }); if (result?.data?.success) { navigate('/families', { replace: true, state: { message: `${familyName || 'Family'} deleted successfully.` } }); } else { setError(result?.data?.message || "Server denied the family deletion request."); } }
        catch (err) {
             setError(`Failed to delete family: ${err.message || "Network/Function error"}`);
        }
    };


    // --- Loading/Error States (remains unchanged) ---
    if (loadingAuth) { return ( <Container fluid className="text-center mt-5"><Spinner animation="border" role="status"><span className="visually-hidden">Loading User...</span></Spinner></Container> ); }
    if (!currentUser) { return ( <Container fluid className="mt-3"><Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view family details.</Alert></Container> ); }
    if (loading) { return ( <Container fluid className="text-center mt-5"><Spinner animation="border" role="status"><span className="visually-hidden">Loading Family Data...</span></Spinner></Container> ); }
    if (error || !family) { return ( <Container fluid className="mt-3"> <Breadcrumb> <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item> <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/families" }}>Families</Breadcrumb.Item> <Breadcrumb.Item active>Error</Breadcrumb.Item> </Breadcrumb> <Alert variant="danger">Error: {error || "Family data could not be loaded."}</Alert> </Container> ); }

    // --- Determine Permissions (remains unchanged) ---
    const canModifyFamilyData = isAdmin || (userFidClaim && family && userFidClaim === family.id);

    // --- Render Page Content ---
    return (
        <Container fluid className="mt-3">
            {/* Breadcrumbs & Action Buttons Row (remains unchanged) */}
            <Row className="mb-3">
                <Col xs={12} md>
                    <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/families" }}>Families</Breadcrumb.Item>
                        <Breadcrumb.Item active>{family.surname || 'Family Details'}</Breadcrumb.Item>
                     </Breadcrumb>
                </Col>
                <Col xs={12} md="auto">
                     {canModifyFamilyData && (
                         <div className="d-flex justify-content-md-end flex-wrap gap-2">
                             <Button variant="success" size="sm" as={Link} to="/members/add" state={{ preselectedFamilyId: family.id, preselectedFamilyName: family.surname }} > <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Member </Button>
                             <Button variant="warning" size="sm" as={Link} to={`/families/edit/${family.id}`} title={`Edit ${family.surname} Family Details`} > <FontAwesomeIcon icon={faPencilAlt} className="me-1" /> Edit Family </Button>
                             <Button variant="info" size="sm" as={Link} to={`/families/${family.id}/assign-photos/all`} title={`Assign Photos for ${family.surname} Family`} > <FontAwesomeIcon icon={faImages} className="me-1" /> Assign Photos </Button>
                             <Button variant="danger" size="sm" title={`Delete ${family.surname} Family`} onClick={() => handleDeleteFamily(family.id, family.surname)} > <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete Family </Button>
                         </div>
                     )}
                 </Col>
            </Row>

             {error && <Row><Col><Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert></Col></Row>}

            <Row className="justify-content-center">
                 <Col md={12}>
                    <Card className="shadow-sm">
                        <Card.Header as="h2">{family.surname || 'N/A'} Family</Card.Header>
                        <Card.Body>
                            {/* Family Photo and Details Row */}
                            <Row className="mb-3">
                                <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
                                    <h5>Photo</h5>
                                    <img
                                       src={photoURL}
                                       alt={`${family.surname || 'Family'} photo`}
                                       className="img-fluid rounded mb-2"
                                       style={{ maxHeight: '250px', width: 'auto', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}
                                       onError={(e) => {
                                            if (e.target.src !== MAIN_FAMILY_PHOTO_PLACEHOLDER) {
                                                e.target.onerror = null;
                                                e.target.src = MAIN_FAMILY_PHOTO_PLACEHOLDER;
                                            }
                                       }}
                                    />
                                </Col>
                                <Col md={6}>
                                    <h5>Details</h5>
                                    <dl className="row">
                                        <dt className="col-sm-4 col-lg-3">Street</dt><dd className="col-sm-8 col-lg-9">{family.street || '-'}</dd>
                                        <dt className="col-sm-4 col-lg-3">City</dt><dd className="col-sm-8 col-lg-9">{family.city || '-'}</dd>
                                        <dt className="col-sm-4 col-lg-3">State</dt><dd className="col-sm-8 col-lg-9">{family.state || '-'}</dd>
                                        <dt className="col-sm-4 col-lg-3">Zip</dt><dd className="col-sm-8 col-lg-9">{family.zipcode || '-'}</dd>
                                        <dt className="col-sm-4 col-lg-3">Phone</dt>
                                        <dd className="col-sm-8 col-lg-9">
                                            <FormattedPhoneNumber phoneNumber={family.phone} fallback="-" />
                                        </dd>
                                        <dt className="col-sm-4 col-lg-3">Anniversary</dt><dd className="col-sm-8 col-lg-9">{family.anniversary instanceof Timestamp ? format(family.anniversary.toDate(), 'MMM d') : (family.anniversary || '-')}</dd>
                                    </dl>
                                </Col>
                            </Row>
                            <hr />

                            {/* Family Members Section */}
                            <div>
                                <h4 className="mb-2">Family Members</h4>
                                {loadingMembers ? ( <div className="text-center py-3"> <Spinner animation="border" size="sm" role="status"/> <span className="ms-2 fst-italic">Loading Members...</span> </div>
                                ) : errorMembers ? ( <Alert variant="warning" size="sm">{errorMembers}</Alert>
                                ) : (
                                    <Table striped bordered hover responsive size="sm" className="align-middle">
                                         <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '60px', textAlign: 'center' }}></th>
                                                <th className="text-start">Name</th>
                                                <th className="text-start" style={{ minWidth: '80px' }}>Birthday</th>
                                                <th className="text-start" style={{ minWidth: '120px' }}>Cellphone</th>
                                                <th className="text-start">Email</th>
                                                <th className="text-center" style={{ width: '80px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {members.length > 0 ? (
                                                members.map(member => {
                                                    const canModifyMember = canModifyFamilyData;
                                                    const birthdayValue = member.birthday instanceof Timestamp ? member.birthday : member.birthdayString;
                                                    const formattedBirthday = formatBirthday(birthdayValue);
                                                    if (!member || !member.id) { return null; }

                                                    // For cache key, assuming member object has 'imageUpdatedAt' (Timestamp)
                                                    // or similar field that changes when photo is updated.
                                                    const photoCacheKey = member.imageUpdatedAt instanceof Timestamp
                                                                          ? member.imageUpdatedAt.toMillis().toString()
                                                                          : (member.imageUpdatedAt || member.id); // Fallback

                                                    return (
                                                        <tr key={member.id}>
                                                            <td className="text-center">
                                                                <MemberPhotoAvatar
                                                                    src={memberOriginalPhotoPaths[member.id]} // Pass the GCS path or direct URL
                                                                    alt={member.name || 'Member'}
                                                                    size={40}
                                                                    cacheKey={photoCacheKey} // Pass the cache key
                                                                />
                                                            </td>
                                                            <td>
                                                            <Link to={`/families/${family.id}/member/${member.id}`}>
                                                            {member.name || 'No Name'}
                                                            </Link>
                                                            </td>
                                                            <td>{formattedBirthday || '-'}</td>
                                                            <td>
                                                                <FormattedPhoneNumber phoneNumber={member.cellphone} fallback="-" />
                                                            </td>
                                                            <td style={{ wordBreak: 'break-all' }}>{member.email || '-'}</td>
                                                            <td className="text-center text-nowrap">
                                                                {canModifyMember ? (
                                                                    <>
                                                                        <Button as={Link} to={`/members/edit/${member.id}`} variant="outline-secondary" size="sm" className="py-0 px-1" title={`Edit ${member.name || 'Member'}`}> <FontAwesomeIcon icon={faPencilAlt} fixedWidth/> </Button>
                                                                        <Button variant="outline-danger" size="sm" className="py-0 px-1 ms-1" title={`Delete ${member.name || 'Member'}`} onClick={() => handleDeleteMember(member.id, member.name)}> <FontAwesomeIcon icon={faTrashAlt} fixedWidth/> </Button>
                                                                    </>
                                                                 ) : <span className="text-muted small">--</span> }
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr><td colSpan="6" className="text-center text-muted fst-italic py-2"> No members listed for this family yet. </td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                 </Col>
            </Row>
        </Container>
    );
}

export default FamilyDetail;
